import React, { useCallback, useContext, useEffect, useState } from 'react';
import { isTouchDevice } from 'utils/checkIsTouchDevice';
import AuctionContext from 'utils/context/AuctionContext';
import { formatFilterPrice } from 'utils/formatFilterPrice';
import { generateSingleOfferLink } from 'utils/generateSingleOfferLink';
import { getStringFromDate } from 'utils/getStringFromDate';
import { getAuctionFiltersFromState, getAuctionValueFromState } from 'utils/state-managment/auction/auctionFilter';
import { AuctionOfferResponseContent } from 'utils/state-managment/auction/auctionOffer';
import { AllModules, AllModuleTypes } from 'utils/types/AllModules';
import { AuctionFiltersStateType } from 'utils/types/Auction';
import { UserReactions } from 'utils/types/OfferData';
import { ReactionType } from 'utils/types/ReactionType';

import OfferActions from 'components/common/Card/common/OfferActions/OfferActions';
import { InteractiveElements } from 'components/common/Card/common/V2InteractiveIconsWrapper/V2InteractiveIconsWrapper';
import { IconEnum } from 'components/common/Icon';
import { SliderImages } from 'components/common/Slider/Slider';
import { TabType } from 'components/common/Tabs/V2Tabs';
import { convertToThumbnailPath, createImagesForSlider } from 'components/functions/imagesFunctions';
import { auctionOfferListFunctions } from 'components/functions/offerListFunctions/auctionOfferListFunctions/auctionOfferLlistFunctions';
import { generateOfferActionsElements } from 'components/functions/shared/generateOfferActionsElements';
import V2OfferList, { RenderList } from 'components/offerList/V2OfferList';

const AuctionOffers = () => {
    const {
        auctionFiltersState,
        handleChange,
        auctionOfferState,
        loadAuctionOffers,
        deleteNote,
        updateNote,
        reactionUpdate,
        loadOfferDetailsAsync,
        loadNotesAsync,
        updateReminder
    } = useContext(AuctionContext);

    const [renderList, setRenderList] = useState<RenderList[]>([]);
    const [moreButtonActiveId, setMoreButtonActiveId] = useState<string | null>(null);
    const [detailsMoreButtonActiveId, setDetailsMoreButtonActiveId] = useState<string | null>(null);

    const handleChangePage = useCallback((currentPage: number, rows = 25) => {
        loadAuctionOffers({ page: currentPage, rows });
    }, [auctionFiltersState]);

    const handleActiveFiltersChange = () => {
        loadAuctionOffers({
            requestData: getAuctionValueFromState(auctionFiltersState),
            page: 1,
            rows: 25
        });
    };

    const handleOlderAdverts = () => {
        const date = auctionFiltersState.offerAdded.from ? new Date(new Date(auctionFiltersState.offerAdded.from).setDate(new Date(auctionFiltersState.offerAdded.from).getDate() - 6)) : null;
        const dateString = date && getStringFromDate(date.toDateString());

        loadAuctionOffers({
            requestData: {
                ...getAuctionValueFromState(auctionFiltersState),
                ...{ offerAddedFrom: !auctionFiltersState.daysAmount ? dateString ?? undefined : undefined },
                ...{ daysAmount: auctionFiltersState.daysAmount ? auctionFiltersState.daysAmount + 6 : undefined }
            },
            page: auctionOfferState.auctionOfferList?.pageable?.pageNumber,
            rows: auctionOfferState.auctionOfferList && auctionOfferState.auctionOfferList.size ? auctionOfferState.auctionOfferList.size : 25
        });
    };

    const onNoteAdd = () => {
        // TODO: INVEST-257 Czy ta funkcja jest potrzebna?
        // if (!showNote && showLastNote) {
        //     showLastNote();
        // }
        //
        // if (!data.note?.noteId) {
        //     openOfferTab('note');
        // }
    };

    const onMessageClick = () => {
        onNoteAdd();
    };

    const handleDelete = (noteId: number, id: number) => {
        deleteNote(id, noteId);
    };

    const handleSaveNote = async (value: string, advertId: number, id?: number) => {
        updateNote(value, advertId, id || 0);
    };

    const toggleContainerDetails = (detailsOpen: boolean, offer: AuctionOfferResponseContent) => {
        if (!detailsOpen) {
            if (offer.reaction && !offer.reaction.viewedStatus) {
                reactionUpdate(offer.auctionId, offer.location!, {
                    ...offer.reaction,
                    openedStatus: true,
                    viewedStatus: true
                } as ReactionType);
            }

            loadOfferDetailsAsync(offer.auctionId);
        }
    };

    const handleChangeReaction = (name: keyof ReactionType, value: boolean, offer: AuctionOfferResponseContent) => {
        const userReaction = Object.assign({}, offer.reaction);

        if (name === 'likeStatus' && userReaction.unlikeStatus && value) {
            userReaction.unlikeStatus = false;
        } else if (name === 'unlikeStatus' && userReaction.likeStatus && value) {
            userReaction.likeStatus = false;
        }

        (userReaction[name] as boolean) = value;
        const city = offer.location;

        reactionUpdate(offer.auctionId, city, userReaction as UserReactions);
    };

    const handleDeleteFilterValue = (filterName: string, value?: string | number | null | string[], index?: number) => {
        const stateField = auctionFiltersState[filterName as keyof AuctionFiltersStateType];

        if (index !== undefined && Array.isArray(stateField)) {
            const newArray = [...stateField] as string[];
            newArray.splice(index, 1);
            handleChange(filterName, newArray);
        } else if (filterName === 'offerAdded') {
            handleChange('dateRangeFilter', false);
            handleChange('daysAmount', 3);
        } else {
            handleChange(filterName, {
                from: '',
                to: ''
            });
        }
    };

    const generateInteractiveElements = (offer: AuctionOfferResponseContent): InteractiveElements[] => {
        const offerLink = generateSingleOfferLink(offer.encryptedId, 'auction');
        const isMoreButtonActive = moreButtonActiveId === offer.auctionId.toString();
        const offerActionsElements = generateOfferActionsElements(offerLink, offer.link);
        const additionalActions = auctionOfferListFunctions.generateAdditionalOfferActionElements(offer, updateReminder as (id: number, type: string) => void);

        return [
            {
                tooltipText: 'Wyświetlono',
                icon: IconEnum.EYE,
                active: offer.reaction?.viewedStatus,
                disabled: offer.reaction?.viewedStatus === undefined,
                onClick: () => handleChangeReaction('viewedStatus', !offer.reaction?.viewedStatus, offer)
            },
            {
                tooltipText: 'Lubię to',
                icon: IconEnum.LIKE,
                active: offer.reaction?.likeStatus,
                onClick: () => handleChangeReaction('likeStatus', !offer.reaction?.likeStatus, offer)
            },
            {
                tooltipText: 'Nie lubię tego',
                icon: IconEnum.DISLIKE,
                active: offer.reaction?.unlikeStatus,
                disabled: offer.reaction?.unlikeStatus === undefined,
                onClick: () => handleChangeReaction('unlikeStatus', !offer.reaction?.unlikeStatus, offer)
            },
            {
                tooltipText: 'Notatki',
                icon: IconEnum.MESSAGE,
                active: offer.numberOfNotes ? offer.numberOfNotes > 0 : false,
                counter: offer.numberOfNotes ? offer.numberOfNotes || 1 : undefined,
                openTabOnClick: TabType.NOTES,
                onClick: onMessageClick
            },
            {
                tooltipText: 'Ogłoszenie nieaktualne',
                icon: IconEnum.BLOCKED,
                active: offer.reaction?.hideStatus,
                disabled: offer.reaction?.hideStatus === undefined,
                onClick: () => handleChangeReaction('hideStatus', !offer.reaction?.hideStatus, offer)
            },
            {
                tooltipText: 'Przeprowadzona rozmowa',
                icon: IconEnum.PHONE,
                active: offer.reaction?.phoneStatus,
                disabled: offer.reaction?.phoneStatus === undefined,
                onClick: () => handleChangeReaction('phoneStatus', !offer.reaction?.phoneStatus, offer)
            },
            {
                tooltipText: 'Umówione spotkanie',
                icon: IconEnum.PEOPLES_3,
                active: offer.reaction?.arrangedStatus,
                disabled: offer.reaction?.arrangedStatus === undefined,
                onClick: () => handleChangeReaction('arrangedStatus', !offer.reaction?.arrangedStatus, offer)
            },
            ...offer.photos && offer.photos.length > 0
                ? [
                    {
                        icon: IconEnum.CAMERA,
                        isModalOpen: true,
                        isLink: true,
                        text: <p>Zobacz zdjęcia</p>,
                        isPhotoBtn: true,
                        visibleDuringModal: true,
                        sliderElements: {
                            photos: createImagesForSlider(offer.photos, offer.module) as SliderImages[],
                            title: offer.title
                        }
                    }
                ]
                : [],
            {
                icon: 'squares' as IconEnum,
                tooltipText: isTouchDevice() ? undefined : 'Więcej',
                onClick: () => isMoreButtonActive ? setMoreButtonActiveId(null) : setMoreButtonActiveId(offer.auctionId.toString()),
                additionalJSXSibling: isMoreButtonActive ? <OfferActions elements={[...offerActionsElements, ...additionalActions]} /> : undefined
            }
        ];
    };

    const generateSquareIcon = (offerId: string, isDetail: boolean, offer: AuctionOfferResponseContent) => {
        const buttonId = isDetail ? `${offerId}-detail` : offerId;
        const isMoreButtonActive = isDetail ? detailsMoreButtonActiveId === buttonId : moreButtonActiveId === buttonId;
        const offerLink = generateSingleOfferLink(offer.encryptedId, 'auction');
        const offerActionsElements = generateOfferActionsElements(offerLink, offer.link);
        const additionalActions = auctionOfferListFunctions.generateAdditionalOfferActionElements(offer, updateReminder as (id: number, type: string) => void);

        return {
            icon: 'squares' as IconEnum,
            tooltipText: isTouchDevice() ? undefined : 'Więcej',
            onClick: () => {
                if (isDetail) {
                    isMoreButtonActive
                        ? setDetailsMoreButtonActiveId(null)
                        : setDetailsMoreButtonActiveId(buttonId);
                } else {
                    isMoreButtonActive
                        ? setMoreButtonActiveId(null)
                        : setMoreButtonActiveId(buttonId);
                }
            },
            additionalJSXSibling: isMoreButtonActive
                ? <OfferActions
                    onClickOutside={() => isDetail ? setDetailsMoreButtonActiveId(null) : setMoreButtonActiveId(null)}
                    isInModal={isDetail}
                    elements={[...offerActionsElements, ...additionalActions]}

                />
                : undefined
        };
    };

    const handleNotes = async (offer: AuctionOfferResponseContent) => {
        loadNotesAsync(offer.auctionId.toString(), AllModules.AUCTION);
    };

    const generateCardData = (offer: AuctionOfferResponseContent) => {
        const subMainValues: React.ReactNode[] = [];

        if (offer.pricePerSquareMeter) {
            subMainValues.push(
                <><b>{offer.pricePerSquareMeter.toString()}</b> zł / m<sup>2</sup></>
            );
        }

        return {
            additionalInfoBadge: offer.auctionDate ? `Data licytacji: ${offer.auctionDate}` : undefined,
            infoElements: auctionOfferListFunctions.generateInfoElements(offer),
            preTitle: offer.location,
            date: offer.dateAdded,
            title: offer.title,
            interactiveElements: generateInteractiveElements(offer),
            note: offer.newestNote,
            numberOfNotes: offer.numberOfNotes ? offer.numberOfNotes : undefined,
            getAllNotes: loadNotesAsync,
            offerId: offer.auctionId.toString(),
            offerModule: offer.module as AllModuleTypes,
            mainValue: offer.price ? `${formatFilterPrice(offer.price)} zł` : '- zł',
            subMainValues,
            images: createImagesForSlider(offer.photos || [], offer.module),
            thumbnailPath: offer.photos && offer.photos.length > 0 ? convertToThumbnailPath(offer.photos[0], offer.module) : '/defaultImg.png',
            link: {
                url: offer.link,
                name: offer.portal
            },
            adContext: { adId: offer.auctionId, adModule: offer.module }
        };
    };

    useEffect(() => {
        const offersList = auctionOfferState.auctionOfferList.content
            ? auctionOfferState.auctionOfferList?.content.map((offer): RenderList => {
                const interactiveElements = generateInteractiveElements(offer);
                interactiveElements.splice(-1);
                const detailsTabContent = auctionOfferListFunctions.generateOfferDetailsTabContent(offer);

                return {
                    id: offer.auctionId,
                    onSingleOfferClick: (_: React.MouseEvent, detailsOpen: boolean) => toggleContainerDetails(detailsOpen, offer),
                    baseCardContent: {
                        bigImage: true,
                        cardData: {
                            ...generateCardData(offer),
                            interactiveElements: [
                                ...interactiveElements,
                                generateSquareIcon(offer.auctionId.toString(), false, offer)
                            ]
                        },
                        openOfferTab: () => {
                        }
                    },
                    detailsCardData: {
                        interactiveElements: [
                            ...interactiveElements,
                            generateSquareIcon(offer.auctionId.toString(), true, offer)
                        ],
                        thumbnailPath: offer.photos && offer.photos.length > 0 ? convertToThumbnailPath(offer.photos[0], offer.module) : '/defaultImg.png',
                        date: offer.dateAdded ? `${offer.dateAdded}` : '',
                        additionalInfoBadge: offer.auctionDate ? `Data licytacji: ${offer.auctionDate}` : undefined,
                        onToggleContainerDetails: () => {
                        },
                        tabsContent: {
                            toggleContainerDetails: () => {
                            },
                            interactiveElements: [
                                ...interactiveElements,
                                generateSquareIcon(offer.auctionId.toString(), true, offer)
                            ],
                            detailsTabContent: { ...detailsTabContent, adContext: { adId: offer.auctionId, adModule: offer.module } },
                            notesTabContent: {
                                notes: auctionOfferState.notes,
                                handleDelete: (noteId: string) => handleDelete(Number(noteId), offer.auctionId),
                                handleSaveNote: (value: string, id?: string) => handleSaveNote(value, offer.auctionId, Number(id))
                            },
                            numberOfNotes: offer.numberOfNotes || 0,
                            handleLoadNotes: () => handleNotes(offer)
                        }
                    }
                };
            })
            : [];

        setRenderList(offersList);
    }, [
        auctionOfferState.auctionOfferList?.content,
        auctionOfferState.notes,
        moreButtonActiveId,
        detailsMoreButtonActiveId
    ]);

    return (
        <div>
            <V2OfferList totalPages={auctionOfferState.auctionOfferList.totalPages} handleChangePage={handleChangePage}
                pageNumber={auctionOfferState.auctionOfferList?.pageable?.pageNumber || 1}
                lastPage={auctionOfferState.auctionOfferList.last}
                handleOlderAdverts={handleOlderAdverts}
                isLoadMoreButton
                handleActiveFiltersChange={handleActiveFiltersChange}
                showList={!auctionOfferState.auctionOfferList.empty && auctionOfferState.auctionOfferList.content?.length > 0}
                filters={getAuctionFiltersFromState(auctionFiltersState)}
                activeFiltersState={auctionFiltersState}
                onDeleteFilter={handleDeleteFilterValue}
                showNoItemsText={auctionOfferState.auctionOfferList && auctionOfferState.auctionOfferList.empty && auctionOfferState.auctionOfferList.content.length === 0 && auctionOfferState.auctionOfferList.afterSearch}
                renderList={renderList}
            />
        </div>
    );
};

export default AuctionOffers;
